import { Box, CopyButton, Stack, Text, UnstyledButton, useMantineTheme } from "@mantine/core";
import { useElementSize } from "@mantine/hooks";
import { ContextModalProps } from "@mantine/modals";
import { QRCodeSVG } from "qrcode.react";
import React, { useState } from "react";
import { MdCheck } from "react-icons/md";
import { Copy } from "src/assets/Icons";
import useAsyncEffect from "use-async-effect"; // Import the useAsyncEffect hook

// Extend PermissionName to include 'clipboard-read' and 'clipboard-write'
type ExtendedPermissionName = PermissionName | "clipboard-read" | "clipboard-write";

export type ModalInnerProps = {
	link: string;
};

export const LinkShareModal = (props: ContextModalProps<ModalInnerProps>) => {
	const {
		innerProps: { link }
	} = props;
	// const params = useParams();
	const { ref, width } = useElementSize();
	const theme = useMantineTheme();

	// State to track if copy is available
	const [isCopyAvailable, setIsCopyAvailable] = useState(false);

	useAsyncEffect(async () => {
		try {
			if (navigator.permissions && navigator.permissions.query) {
				const permissionStatus = await navigator.permissions.query({
					name: "clipboard-read" as ExtendedPermissionName as unknown as PermissionName
				});

				log.info("clipboard-read permission state is:", permissionStatus.state);

				setIsCopyAvailable(permissionStatus.state === "granted" || permissionStatus.state === "prompt");
			} else {
				setIsCopyAvailable(!!navigator.clipboard);
			}
		} catch (error) {
			log.warn("Error checking clipboard-read permission:", error);
			setIsCopyAvailable(false);
		}
	}, []); // Empty dependency array to run once on mount

	// Стили для кнопки вынесены для читаемости
	const buttonStyles: React.CSSProperties = {
		minWidth: width,
		// theme.spacing.sm -> var(--mantine-spacing-sm)
		padding: "var(--mantine-spacing-sm)",
		gap: "var(--mantine-spacing-sm)",
		// theme.radius.md -> var(--mantine-radius-md)
		borderRadius: "var(--mantine-radius-md)",
		display: "flex",
		alignItems: "center",
		transition: "100ms background-color ease"
		// Псевдо-селекторы :hover и :active не работают в инлайн-стилях.
		// :active обрабатывается самим UnstyledButton через data-атрибут data-active.
		// :hover лучше всего реализовывать через CSS-модули или data-атрибуты.
		// Здесь мы упрощаем, оставляя только изменение цвета границы и фона при копировании.
	};

	return (
		<Stack style={{ height: "50vh" }} align="center">
			<Box ref={ref} style={{ flexGrow: 1, width: "100%", minHeight: 0, display: "flex" }}>
				<QRCodeSVG
					style={{ flexBasis: "100%" }}
					height={undefined}
					width={undefined}
					value={link}
					fgColor={theme.colors.dark[0]}
					bgColor={theme.colors.dark[7]}
				/>
			</Box>
			<CopyButton value={link}>
				{({ copied, copy }) => (
					<UnstyledButton
						component={isCopyAvailable ? "button" : "div"}
						style={{
							...buttonStyles,
							// Динамические стили, зависящие от состояния
							border: `1px solid var(--mantine-color-${copied ? "green" : "dark"}-2)`,
							backgroundColor: copied ? "var(--mantine-color-green-filled)" : undefined,
							cursor: !isCopyAvailable ? "auto" : "pointer"
						}}
						onClick={isCopyAvailable ? copy : undefined}
						disabled={!isCopyAvailable}
					>
						<Text ta="center" style={{ flexGrow: 1 }}>
							{link}
						</Text>
						{isCopyAvailable && <>{copied ? <MdCheck size={24} /> : <Copy width={24} height={24} />}</>}
					</UnstyledButton>
				)}
			</CopyButton>
		</Stack>
	);
};
